import { useCallback, useRef, useState } from 'react';
import { captureEvent, captureException } from './telemetry/posthog/frontend';
import { buildLiveInterviewBaseProps } from './telemetry/live-interview-contract';
import { emitToParent } from './embed/postMessage';
import type { InvitePrefillData } from '../types';
import { parseConversationConfig, type ConversationExperienceConfig } from './multimodal-contract';

/**
 * User-visible message when the start attempt fails. emitToParent is a
 * no-op outside iframes, so direct-link respondents rely on this string
 * being rendered near the Start button.
 */
export const START_CALL_ERROR_MESSAGE = 'Could not start. Please try again.';

/** Inputs for {@link useStartCall}. */
export interface UseStartCallOptions {
  /** Flow id for telemetry, read at call time so it is never stale. */
  getRoomId: () => string;
  /** Room id sent to the create-response API. */
  effectiveRoomId: string;
  /** Passcode sent to the create-response API. */
  effectivePasscode: string;
  /** Parent origin when running embedded; null for direct links. */
  embedOrigin: string | null;
  /** Optional details carried by a direct invitation link. */
  prefillData?: InvitePrefillData;
  /** Obtain a fresh single-use verification token for embedded enrollment. */
  getTurnstileToken?: () => Promise<string | undefined>;
  /** Existing response row id, when resuming an enrolled conversation. */
  interviewDataId: string | null;
  /**
   * Campaign-automation participant token, forwarded to create-response so the
   * anonymously created response links back to the participant. Null when the
   * respondent did not arrive via a personalized invite link.
   */
  participantToken?: string | null;
  /** Persists the anonymous response row created during preflight. */
  onResponseCreated: (interviewDataId: string, responseNumber?: number, responseToken?: string, conversationConfig?: ConversationExperienceConfig) => void;
  /** Restore accepted invitation details in the live UI after enrollment. */
  onIdentityResolved?: (identity: InvitePrefillData) => void;
  /** Transitions the app into the call once preflight succeeds. */
  onStarted: () => void;
}

/** Return shape of {@link useStartCall}. */
export interface UseStartCallResult {
  /** Click handler for the Start button. Safe against double-clicks. */
  startCall: (options?: { restartHistorical?: boolean }) => Promise<void>;
  /** True while a start attempt is in flight; disable the Start button. */
  starting: boolean;
  /** User-visible failure message, cleared on the next attempt. */
  error: string | null;
  /** False when repeating enrollment cannot resolve the returned failure. */
  canRetry: boolean;
  /** A historical response remains untouched until the respondent starts anew. */
  restartRequired: boolean;
}

/**
 * Drives enrollment and the respondent "Start" action: creates the response
 * row when no enrolled response exists,
 * guards against double-submission (a duplicate create-response consumes a
 * max_responses slot), and surfaces failures both to the embed parent and
 * as a visible error for direct-link respondents.
 */
export function useStartCall(options: UseStartCallOptions): UseStartCallResult {
  const {
    getRoomId,
    effectiveRoomId,
    effectivePasscode,
    embedOrigin,
    prefillData,
    getTurnstileToken,
    interviewDataId,
    participantToken,
    onResponseCreated,
    onIdentityResolved,
    onStarted,
  } = options;

  const [starting, setStarting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [canRetry, setCanRetry] = useState(true);
  const [restartRequired, setRestartRequired] = useState(false);
  // Ref, not state: the guard must trip synchronously on a double-click,
  // before React re-renders with `starting` set.
  const inFlightRef = useRef(false);

  const startCall = useCallback(async (attempt?: { restartHistorical?: boolean }) => {
    if (inFlightRef.current || !canRetry) return;
    const roomId = getRoomId();
    if (roomId.length < 1) return;

    inFlightRef.current = true;
    setStarting(true);
    setError(null);
    captureEvent('live_interview.start_clicked', buildLiveInterviewBaseProps(roomId, 'preflight'));

    try {
      // Enrollment is independent of identity questions and applies equally to
      // direct links and embeds. Existing response grants are reused unchanged.
      if (!interviewDataId) {
        try {
          const requestBody: Record<string, unknown> = {
            room_id: effectiveRoomId,
            passcode: effectivePasscode,
          };
          if (embedOrigin) {
            requestBody.embed_origin = embedOrigin;
            requestBody.turnstile_token = await getTurnstileToken?.();
          }
          if (participantToken) {
            requestBody.participant_token = participantToken;
            requestBody.participant_identity_only = true;
          } else if (prefillData) {
            requestBody.interviewee_name = prefillData.name?.trim() || undefined;
            requestBody.interviewee_email = prefillData.email?.trim() || undefined;
            requestBody.interviewee_job = prefillData.job?.trim() || undefined;
            requestBody.interviewee_company = prefillData.company?.trim() || undefined;
            requestBody.interviewee_linkedin = prefillData.linkedin?.trim() || undefined;
            requestBody.interviewee_company_url = prefillData.company_url?.trim() || undefined;
            requestBody.interviewee_metadata = prefillData.metadata;
          }
          if (attempt?.restartHistorical) requestBody.restart_historical = true;
          const res = await fetch('/api/room/create-response', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(requestBody),
          });
          if (!res.ok) {
            const problem = await res.json().catch(() => null) as { error_code?: unknown } | null;
            if (res.status === 409 && problem?.error_code === 'conversation_restart_required') {
              setRestartRequired(true);
              return;
            }
            if (res.status >= 400 && res.status < 500 && res.status !== 429 && problem?.error_code !== 'turnstile_failed') {
              setCanRetry(false);
              setError('This link cannot start a conversation. Contact the person who shared this link.');
              captureEvent('live_interview.join_rejected', {
                ...buildLiveInterviewBaseProps(roomId, 'preflight'), reason: 'room_unavailable', status: res.status,
              });
              emitToParent('error', { stage: 'create_response', status: res.status });
              return;
            }
            captureException(
              new Error(`Anonymous create-response failed: ${res.status}`),
              'useStartCall.createAnonymousResponse',
              { roomId, status: res.status },
            );
            emitToParent('error', { stage: 'create_response', status: res.status });
            setError(START_CALL_ERROR_MESSAGE);
            return;
          }
          const data = await res.json() as {
            interview_data_id?: string; response_number?: number;
            response_token?: string; conversation_config?: unknown;
            interviewee_name?: string; interviewee_email?: string; interviewee_company?: string;
          };
          if (!data.interview_data_id) {
            emitToParent('error', { stage: 'create_response', reason: 'no_id' });
            setError(START_CALL_ERROR_MESSAGE);
            return;
          }
          const config = parseConversationConfig(data.conversation_config);
          if (!config || typeof data.response_token !== 'string' || !/^[a-f0-9]{64}$/.test(data.response_token)) {
            captureException(new Error('Invalid conversation access contract'), 'useStartCall.createAnonymousResponse', { roomId });
            setError(START_CALL_ERROR_MESSAGE);
            return;
          }
          setRestartRequired(false);
          onResponseCreated(data.interview_data_id, data.response_number, data.response_token, config);
          onIdentityResolved?.({
            ...(participantToken ? {} : prefillData),
            name: data.interviewee_name ?? (participantToken ? undefined : prefillData?.name),
            email: data.interviewee_email ?? (participantToken ? undefined : prefillData?.email),
            company: data.interviewee_company ?? (participantToken ? undefined : prefillData?.company),
          });
        } catch (err) {
          captureException(
            err instanceof Error ? err : String(err),
            'useStartCall.createAnonymousResponse',
            { roomId },
          );
          emitToParent('error', { stage: 'create_response', reason: 'network' });
          setError(START_CALL_ERROR_MESSAGE);
          return;
        }
      }

      emitToParent('response_started');
      onStarted();
    } finally {
      inFlightRef.current = false;
      setStarting(false);
    }
  }, [
    getRoomId,
    effectiveRoomId,
    effectivePasscode,
    embedOrigin,
    prefillData,
    getTurnstileToken,
    canRetry,
    interviewDataId,
    participantToken,
    onResponseCreated,
    onIdentityResolved,
    onStarted,
  ]);

  return { startCall, starting, error, canRetry, restartRequired };
}
